import { IExperienceAction, IExperienceSession } from '../../../interfaces/experience';
import { Avatar, Button, Card, Col, Form, Input, Row, Segmented, Space, Typography } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  MailOutlined,
  ManOutlined,
  MessageOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ShowButton, useForm } from '@refinedev/antd';
import { ExperienceActionSelect } from '../../../components/form-item/ExperienceActionSelect';
import { ImageUpload } from '../../../components/form-item/ImageUpload';
import { SitterSelect } from '../../../components/form-item/SitterSelect';
import { HttpError, useMany } from '@refinedev/core';
import { useEffect } from 'react';

const fields = [
  'id',
  'status',
  'memo',
  'sitterId',
  {
    user: [
      'id',
      'email',
      {
        profile: ['name', 'tel', 'gender'],
      },
    ],
    child: [
      'id',
      'name',
      'gender',
      'birthday',
      'ageYear',
      {
        avatar: ['id', 'url'],
      },
    ],
    reservation: [
      'id',
      'status',
      'remark',
      'parentTel',
      'parentName',
      'parentKana',
      'email',
      'seatCount',
      {
        reservationSlot: ['name', 'price', 'date', 'startTime', 'endTime', 'timeLength'],
      },
    ],
    sessionActions: ['id', 'actionId', 'episode', 'order', 'title', 'description', 'advice'],
    sessionImages: [
      'id',
      'title',
      'order',
      {
        image: ['id', 'url'],
      },
    ],
  },
];

type FormValues = {
  memo: string;
  sitterId: string;
  status: string;
  sessionActions: {
    id: string;
    actionId: string;
    episode: string;
    order: number;
    title: string;
    description: string;
    advice: string;
  }[];
  sessionImages: {
    id: string;
    title: string;
    order: number;
    image: {
      id: string;
      url: string;
    };
  }[];
};

export const ExperienceSessionShow = () => {
  const { queryResult, mutationResult, formProps, form, onFinish } = useForm<
    IExperienceSession,
    HttpError,
    FormValues
  >({
    resource: 'experienceSessions',
    action: 'edit',
    redirect: false,
    meta: {
      fields,
    },
    mutationMeta: {
      fields: [{ experienceSession: fields }],
    },
  });
  const isLoading = queryResult?.isLoading && mutationResult?.isLoading;
  const record = mutationResult?.data?.data || queryResult?.data?.data;

  // セッションの行動を追加する
  const actions = (Form.useWatch('sessionActions', form) as FormValues['sessionActions']) || [];
  const { data } = useMany<IExperienceAction>({
    resource: 'experienceActions',
    ids: actions.map((action) => action.actionId),
    queryOptions: {
      enabled: !!actions.length,
    },
    meta: {
      fields: ['id', 'name', 'title', 'description', 'advice'],
    },
  });
  useEffect(() => {
    actions.map((action, index) => {
      const newAction = data?.data?.find((a) => a.id === action.actionId);
      if (!action.title) form.setFieldValue(['sessionActions', index, 'title'], newAction?.title);
      if (!action.description)
        form.setFieldValue(['sessionActions', index, 'description'], newAction?.description);
      if (!action.advice)
        form.setFieldValue(['sessionActions', index, 'advice'], newAction?.advice);
    });
  }, [data]);

  // Submit前にOrderを入れる
  const handleOnFinish = (values: FormValues) => {
    void onFinish({
      ...values,
      sessionActions: values.sessionActions.map((sessionAction, index) => ({
        ...sessionAction,
        order: index + 1,
      })),
      sessionImages: values.sessionImages.map((sessionImage, index) => ({
        ...sessionImage,
        order: index + 1,
      })),
    });
  };

  return (
    <Form {...formProps} onFinish={handleOnFinish} layout="vertical">
      <Row gutter={[16, 16]} style={{}}>
        <Col xl={8} lg={24} xs={24}>
          <Card
            dir="column"
            loading={isLoading}
            size="small"
            title="ユーザー"
            extra={<ShowButton resource="users" recordItemId={record?.user.id} size="small" />}
          >
            <Space direction="vertical">
              <Typography.Text>
                <MailOutlined /> {record?.user.email}
              </Typography.Text>
              <Typography.Text>
                <UserOutlined />{' '}
                {record?.user.profile
                  ? `${record?.user.profile?.name}(${record?.user.profile.kana || '未設定'})`
                  : '未設定'}
              </Typography.Text>
              <Typography.Text>
                <ManOutlined />{' '}
                {record?.user.profile
                  ? record.user.profile.gender === 'male'
                    ? '男性'
                    : '女性'
                  : '未設定'}
              </Typography.Text>
            </Space>
          </Card>
        </Col>

        <Col xl={8} lg={24} xs={24}>
          <Card
            title="子供"
            size="small"
            extra={<ShowButton resource="children" recordItemId={record?.child.id} size="small" />}
          >
            <Space direction="vertical">
              <Avatar
                shape="square"
                size={128}
                icon={<UserOutlined />}
                src={record?.child.avatar.url}
              />
              <Typography.Text>
                <UserOutlined /> {record?.child.name}
              </Typography.Text>
              <Typography.Text>
                <ManOutlined /> {record?.child.gender}
              </Typography.Text>
              <Typography.Text>
                <CalendarOutlined />{' '}
                {record?.child.birthday
                  ? `${record?.child.birthday} (${record?.child.ageYear}歳)`
                  : '未設定'}
              </Typography.Text>
            </Space>
          </Card>
        </Col>

        <Col xl={8} lg={24} xs={24}>
          <Card
            dir="column"
            title="予約"
            size="small"
            extra={
              <ShowButton
                resource="experience-reservations"
                recordItemId={record?.reservation.id}
                size="small"
              />
            }
            loading={isLoading}
          >
            <Space direction="vertical">
              <Segmented
                options={[
                  { label: '予約済み', value: 'reserved' },
                  { label: '参加中', value: 'started' },
                  { label: '参加済み', value: 'finished' },
                ]}
                value={record?.reservation.status}
              />
              <Typography.Text>
                <CalendarOutlined /> {record?.reservation.reservationSlot.date}
              </Typography.Text>
              <Typography.Text>
                <ClockCircleOutlined />{' '}
                {record
                  ? `${record?.reservation.reservationSlot.startTime} ~ ${
                      record?.reservation.reservationSlot.endTime
                    } (${record.reservation.reservationSlot.timeLength}分)`
                  : '未設定'}
              </Typography.Text>
              <Typography.Text>
                <TeamOutlined /> {record?.reservation.seatCount}枠
              </Typography.Text>
              <Typography.Text>
                <MessageOutlined /> {record?.reservation.remark || 'なし'}
              </Typography.Text>
              <Typography.Title level={5}>緊急連絡先</Typography.Title>
              <Typography.Text>
                {record?.reservation
                  ? `${record.reservation.parentName} (${record.reservation.parentKana})`
                  : '未設定'}
              </Typography.Text>
              <Typography.Text>{record?.reservation?.parentTel || '未設定'}</Typography.Text>
              <Typography.Text>{record?.reservation?.email || '未設定'}</Typography.Text>
            </Space>
          </Card>
        </Col>

        <Col xl={24} lg={24} xs={24}>
          <Card
            title="発達特徴"
            extra={
              <Space align="end">
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  保存
                </Button>
                <Button onClick={() => form.resetFields()}>リセット</Button>
              </Space>
            }
            headStyle={{ position: 'sticky', top: 0, zIndex: 99, background: 'white' }}
          >
            <Row gutter={[16, 16]} style={{}}>
              <Col xl={24} lg={24} xs={24}>
                <Row gutter={[16, 16]} style={{}}>
                  <Col xl={12} lg={24} xs={24}>
                    <Form.Item name="status">
                      <Segmented
                        options={[
                          { label: '参加前', value: 'upcoming' },
                          { label: '参加中', value: 'started' },
                          { label: '参加済み', value: 'finished' },
                          { label: 'プレビュー', value: 'preview' },
                          { label: '提出済み', value: 'reviewed' },
                          { label: 'キャンセル', value: 'canceled' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item name="sitterId" label="シッター">
                      <SitterSelect />
                    </Form.Item>
                  </Col>
                  <Col xl={12} lg={24} xs={24}>
                    <Form.Item name="memo" label="メモ">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col xl={12} lg={24} xs={24}>
                <Form.List name="sessionActions">
                  {(fields, { add, remove }) => (
                    <Card
                      dir="column"
                      size="small"
                      loading={isLoading}
                      title="行動"
                      extra={
                        <Button
                          onClick={() =>
                            add({
                              episode: '',
                              actionId: '',
                              title: '',
                              description: '',
                              advice: '',
                            })
                          }
                        >
                          追加
                        </Button>
                      }
                    >
                      <Row gutter={[4, 4]} style={{}}>
                        {fields.map((field, index) => {
                          return (
                            <Col xl={24} lg={24} xs={24} key={field.key}>
                              <Card
                                dir="column"
                                size="small"
                                title={index + 1}
                                extra={<Button onClick={() => remove(field.name)}>削除</Button>}
                              >
                                <Form.Item name={[field.name, 'episode']} label="エピソード">
                                  <Input.TextArea rows={3} />
                                </Form.Item>
                                <Form.Item name={[field.name, 'actionId']} label="行動">
                                  <ExperienceActionSelect />
                                </Form.Item>
                                <Form.Item name={[field.name, 'title']} label="タイトル">
                                  <Input.TextArea rows={3} />
                                </Form.Item>
                                <Form.Item name={[field.name, 'description']} label="説明">
                                  <Input.TextArea rows={3} />
                                </Form.Item>
                                <Form.Item name={[field.name, 'advice']} label="アドバイス">
                                  <Input.TextArea rows={3} />
                                </Form.Item>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  )}
                </Form.List>
              </Col>

              <Col xl={12} lg={24} xs={24}>
                <Form.List name="sessionImages">
                  {(fields, { add, remove, move }) => (
                    <Card
                      dir="column"
                      size="small"
                      loading={isLoading}
                      title="画像"
                      extra={<Button onClick={() => add({ image: '', title: '' })}>追加</Button>}
                    >
                      <Row gutter={[4, 4]} style={{}}>
                        {fields.map((field, index) => {
                          return (
                            <Col xl={24} lg={24} xs={24} key={field.key}>
                              <Card
                                dir="column"
                                size="small"
                                title={index + 1}
                                extra={<Button onClick={() => remove(field.name)}>削除</Button>}
                              >
                                <Form.Item name={[field.name, 'image']}>
                                  <ImageUpload />
                                </Form.Item>
                                <Form.Item name={[field.name, 'title']} label="タイトル">
                                  <Input.TextArea rows={3} />
                                </Form.Item>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  )}
                </Form.List>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};
